import React, { useMemo } from 'react'
import CopyIcon from 'transfix-icons/build/CopyIcon'
import { Badge } from 'transfix-ui/components/Badge'
import { Button } from 'transfix-ui/components/Button'
import ContextCard from 'transfix-ui/components/ContextCard'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { makeStyles, Typography } from 'transfix-ui/core'
import { useCopyToClipboard } from 'transfix-ui/hooks/useCopyToClipboard'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import SavedSearches from '../SavedSearches'
import { ISavedSearch } from '../SavedSearches/types'
import { getItemNumberInterval } from './utils/getItemNumberInterval'

export interface ISummaryProps<Filters> {
  totalOptions: number
  filtersSummary?: (filters: Partial<Filters>, options?: Record<string, boolean>) => string
  filters: Partial<Filters>
  onSaveSearch?: (filters: Partial<Filters>, filterName: string) => void
  onRemoveSearch?: (id: string) => void
  title: string
  showCurrentElements?: boolean
  pageNumber: number
  itemsPerPage: number
  savedSearches?: ISavedSearch[]
  /**
   * When true, clicking save search button will show the user the option
   * to copy the current page url for future use instead of being able to
   * save multiple search preferences.
   * @optional
   * @default true
   */
  disableSavedSearch?: boolean
  onSaveDefault?: (newDefault: ISavedSearch) => void
}

const useSummaryStyles = makeStyles(theme => ({
  summary: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  contextCard: {
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'column',
    width: 272,
  },
  separator: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    paddingTop: theme.spacing(1),
  },
  elements: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}))

export function Summary<Filters>({
  disableSavedSearch = true,
  filters,
  filtersSummary,
  itemsPerPage,
  onRemoveSearch,
  onSaveDefault,
  onSaveSearch,
  pageNumber,
  savedSearches,
  showCurrentElements = false,
  title,
  totalOptions,
}: ISummaryProps<Filters>) {
  const styles = useSummaryStyles()
  const enableMultiSaveSearches = useFeatureFlag('mpIssuePageListSaveSearches')

  const [, copyText] = useCopyToClipboard()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const summary = useMemo(() => {
    if (filtersSummary) {
      return filtersSummary(filters)
    }
    return ''
  }, [filtersSummary, filters])

  const copyCurrentURL = () => {
    copyText(window.location.href)
    openToast({
      toastType: 'default',
      toastMessage: 'URL copied',
    })
  }

  const saveSearchFiltersHandler = () => {
    if (onSaveSearch) {
      onSaveSearch(filters, summary)
    }
  }

  const removeSearchFiltersHandler = (id: string) => {
    if (onRemoveSearch) {
      onRemoveSearch(id)
    }
  }

  const itemNumbers = getItemNumberInterval(totalOptions, itemsPerPage, pageNumber)

  return (
    <div className={styles.summary}>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      <div className={styles.elements}>
        {showCurrentElements && (
          <>
            <Badge label={itemNumbers} size='medium' />
            <Typography variant='body1'>of</Typography>
          </>
        )}

        <Typography variant='body1'>
          {totalOptions} {title}
        </Typography>
      </div>
      {summary && (
        <Typography variant='caption' color='textSecondary'>
          {summary}
        </Typography>
      )}
      {(disableSavedSearch || !enableMultiSaveSearches) && (
        <ContextCard
          title='Save Search'
          triggerText={<Button color='tertiary'>+ save search</Button>}>
          <div className={styles.contextCard}>
            {summary && <Typography variant='subtitle1'>{summary}</Typography>}
            {onSaveSearch && !enableMultiSaveSearches && (
              <>
                <Button color='primary' onClick={saveSearchFiltersHandler}>
                  Save as Default for Page
                </Button>
              </>
            )}
            <Typography variant='subtitle2' color='textSecondary'>
              (every time you visit this page, these filters will be the default results)
            </Typography>
            <div className={styles.separator}>
              <Button color='tertiary' endIcon={<CopyIcon />} onClick={copyCurrentURL}>
                Copy URL for Future Use
              </Button>
            </div>
          </div>
        </ContextCard>
      )}
      {!disableSavedSearch && summary && enableMultiSaveSearches && (
        <SavedSearches
          onSave={saveSearchFiltersHandler}
          onRemove={removeSearchFiltersHandler}
          searches={savedSearches || []}
          clickToSave
          onSaveDefault={onSaveDefault}
        />
      )}
    </div>
  )
}
